import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import MarkdownEditor from '../components/MarkdownEditor';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminHelp = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [regles, setRegles] = useState('');
  const [legal, setLegal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchHelp();
  }, []);

  const fetchHelp = async () => {
    try {
      const [reglesRes, legalRes] = await Promise.all([
        axios.get(`${API_URL}/help/regles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/help/legal`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRegles(reglesRes.data.content || '');
      setLegal(legalRes.data.content || '');
    } catch (error) {
      console.error('Erreur chargement aide:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (section, content) => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/help`,
        { section, content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Section "${section === 'regles' ? 'Règles' : 'Légal'}" mise à jour`);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 bg-sand-50">
      {/* Header */}
      <div className="bg-sage-500 px-6 py-8 text-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/admin/contents')}
          className="text-white hover:bg-sage-600 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-4xl font-serif font-light mb-2">Page Aide</h1>
        <p className="text-white/80 font-sans">Gérer les règles d'écoute et les mentions légales</p>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <Tabs defaultValue="regles" className="w-full">
          <TabsList className="w-full mb-6">
            <TabsTrigger value="regles" className="flex-1">Règles d'écoute</TabsTrigger>
            <TabsTrigger value="legal" className="flex-1">Mentions Légales</TabsTrigger>
          </TabsList>

          <TabsContent value="regles" className="space-y-4">
            <div className="bg-white rounded-xl border border-sand-200 p-6">
              <h3 className="font-sans font-semibold text-sage-700 text-lg mb-4">
                Règles d'écoute des audios
              </h3>
              <MarkdownEditor
                value={regles}
                onChange={setRegles}
                placeholder="# Règles d'écoute&#10;&#10;Environnement, position, conseils..."
                minHeight="500px"
              />
              <div className="mt-6">
                <Button
                  onClick={() => handleSave('regles', regles)}
                  disabled={saving}
                  className="w-full bg-sage-500 hover:bg-sage-600 text-white h-12 rounded-xl"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer les règles'}
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="legal" className="space-y-4">
            <div className="bg-white rounded-xl border border-sand-200 p-6">
              <h3 className="font-sans font-semibold text-sage-700 text-lg mb-4">
                Mentions légales et conditions d'utilisation
              </h3>
              <MarkdownEditor
                value={legal}
                onChange={setLegal}
                placeholder="# Mentions légales&#10;&#10;Note santé, usage personnel, propriété intellectuelle..."
                minHeight="500px"
              />
              <div className="mt-6">
                <Button
                  onClick={() => handleSave('legal', legal)}
                  disabled={saving}
                  className="w-full bg-sage-500 hover:bg-sage-600 text-white h-12 rounded-xl"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer les mentions légales'}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Info */}
      <div className="px-6 py-4">
        <div className="bg-sage-50 rounded-xl p-4 border border-sage-100">
          <p className="text-sm text-sage-700 font-sans leading-relaxed">
            <strong>💡 Note :</strong> Ces contenus seront affichés dans la page "Aide" de l'application. Utilisez le Markdown pour structurer vos textes (titres, listes, gras, etc.).
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminHelp;
